import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';
import { FileText, FileSpreadsheet, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getCurrencySymbol } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reports = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalRevenue: 0,
    averageTicket: 0
  });
  const [currency, setCurrency] = useState('BRL');

  useEffect(() => {
    fetchSales();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCurrency(response.data.currency || 'BRL');
    } catch (error) {
      console.error('Erro ao carregar configurações');
    }
  };

  const fetchSales = async () => {
    try {
      const response = await axios.get(`${API}/sales`);
      const salesData = response.data;
      setSales(salesData);

      const totalRevenue = salesData.reduce((sum, sale) => sum + sale.total, 0);
      setStats({
        totalSales: salesData.length,
        totalRevenue,
        averageTicket: salesData.length > 0 ? totalRevenue / salesData.length : 0
      });
    } catch (error) {
      console.error('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/sales/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendas_completo.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar CSV');
    }
  };

  const handleExportCSVSummary = async () => {
    try {
      const response = await axios.get(`${API}/sales/export/csv-summary`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendas_totalizado.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar CSV totalizado');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API}/sales/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendas_completo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar PDF');
    }
  };

  const handleExportPDFSummary = async () => {
    try {
      const response = await axios.get(`${API}/sales/export/pdf-summary`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendas_totalizado.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar PDF totalizado');
    }
  };

  const handleClearSales = async () => {
    const confirmed = window.confirm(
      'ATENÇÃO: Isso irá deletar TODAS as vendas permanentemente. Esta ação não pode ser desfeita. Deseja continuar?'
    );
    
    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      'Tem certeza absoluta? Todas as vendas serão apagadas.'
    );

    if (!doubleConfirm) return;

    try {
      await axios.delete(`${API}/sales/clear`);
      alert('Todas as vendas foram deletadas com sucesso!');
      await fetchSales();
    } catch (error) {
      console.error('Erro ao deletar vendas');
      alert('Erro ao deletar vendas. Tente novamente.');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR');
  };

  const currencySymbol = getCurrencySymbol(currency);

  if (loading) {
    return (
      <AdminLayout title="Relatórios">
        <div className="text-white/60 font-inter">Carregando...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Relatórios">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-white/60 font-inter">Relatório de vendas e estatísticas</p>
          <Button
            data-testid="clear-sales-button"
            onClick={handleClearSales}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Zerar Vendas
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
            <p className="text-white/60 font-inter text-sm mb-2">Total de Vendas</p>
            <p className="text-4xl font-bold font-oswald text-white">{stats.totalSales}</p>
          </div>
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
            <p className="text-white/60 font-inter text-sm mb-2">Receita Total</p>
            <p className="text-4xl font-bold font-oswald text-[#FF5500]">{currencySymbol} {stats.totalRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
            <p className="text-white/60 font-inter text-sm mb-2">Ticket Médio</p>
            <p className="text-4xl font-bold font-oswald text-white">{currencySymbol} {stats.averageTicket.toFixed(2)}</p>
          </div>
        </div>

        <Tabs defaultValue="complete" className="w-full">
          <TabsList className="bg-[#18181b] border border-white/10">
            <TabsTrigger value="complete" className="data-[state=active]:bg-[#FF5500]">Relatório Completo</TabsTrigger>
            <TabsTrigger value="summary" className="data-[state=active]:bg-[#FF5500]">Relatório Totalizado</TabsTrigger>
          </TabsList>

          <TabsContent value="complete" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-white/60 font-inter">Relatório detalhado de todas as vendas</p>
              <div className="flex gap-3">
                <Button
                  data-testid="export-csv-button"
                  onClick={handleExportCSV}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV Completo
                </Button>
                <Button
                  data-testid="export-pdf-button"
                  onClick={handleExportPDF}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF Completo
                </Button>
              </div>
            </div>

            <div className="bg-[#18181b] border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#27272a] border-b border-white/10">
                    <tr>
                      <th className="px-6 py-4 text-left font-oswald text-white">Data/Hora</th>
                      <th className="px-6 py-4 text-left font-oswald text-white">Vendedor</th>
                      <th className="px-6 py-4 text-left font-oswald text-white">Itens</th>
                      <th className="px-6 py-4 text-right font-oswald text-white">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map(sale => (
                      <tr
                        key={sale.id}
                        data-testid={`sale-row-${sale.id}`}
                        className="border-b border-white/5 hover:bg-white/5 transition-all"
                      >
                        <td className="px-6 py-4 font-inter text-white/80">{formatDate(sale.created_at)}</td>
                        <td className="px-6 py-4 font-inter text-white">{sale.seller_name}</td>
                        <td className="px-6 py-4 font-inter text-white/60">
                          <div className="space-y-1">
                            {sale.items.map((item, idx) => (
                              <div key={idx} className="text-sm">
                                {item.quantity}x {item.name}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-oswald font-bold text-[#FF5500]">
                          {currencySymbol} {sale.total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="summary" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-white/60 font-inter">Relatório totalizado por produto</p>
              <div className="flex gap-3">
                <Button
                  data-testid="export-csv-summary-button"
                  onClick={handleExportCSVSummary}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV Totalizado
                </Button>
                <Button
                  data-testid="export-pdf-summary-button"
                  onClick={handleExportPDFSummary}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF Totalizado
                </Button>
              </div>
            </div>

            <div className="bg-[#18181b] border border-white/10 rounded-xl p-6">
              <p className="text-white/60 font-inter mb-4">
                Exporte o relatório totalizado em CSV ou PDF para visualizar a quantidade total vendida de cada produto.
              </p>
              <div className="bg-[#27272a] border border-white/10 rounded-lg p-4">
                <h4 className="font-oswald text-white mb-2">O relatório totalizado contém:</h4>
                <ul className="list-disc list-inside text-white/60 font-inter space-y-1">
                  <li>Nome do produto</li>
                  <li>Quantidade total vendida</li>
                  <li>Valor total arrecadado por produto</li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {sales.length === 0 && (
          <div className="text-center py-12 text-white/40 font-inter">
            Nenhuma venda registrada ainda.
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Reports;